// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/hooks/useExtrasPreview.ts
// 🔧 PURPOSE: Il catalogo degli extra per la Home: prezzi "da", il Cestino
//             della Nonna, il late check-out, l'orario di uscita vero, il
//             taxi già preso. Stava dentro BuyableTiles; adesso lo usano
//             tutti i pezzi della Home che ne hanno bisogno.
//
//             Si legge una volta e si tiene in memoria per un minuto (una
//             copia per tutta l'app, per token e lingua): la Home si apre
//             spesso, il listino cambia di rado. Un acquisto o un cambio
//             del listino (evento `nva_extras_update`) lo ricarica subito.
//             Senza token (ospite non entrato) non si chiede niente.
// ============================================

import { useEffect, useState } from 'react';
import { fetchBookingExtras, type ExtrasCatalog } from '../lib/guestApi';

let cached: { key: string; at: number; catalog: ExtrasCatalog } | null = null;
const CACHE_MS = 60 * 1000;

export const useExtrasPreview = (token: string | null | undefined, lang: string): ExtrasCatalog | null => {
  const key = `${token || ''}|${lang}`;
  const [catalog, setCatalog] = useState<ExtrasCatalog | null>(
    () => (token && cached && cached.key === key ? cached.catalog : null),
  );
  useEffect(() => {
    if (!token) { setCatalog(null); return; }
    let alive = true;
    const load = async () => {
      if (cached && cached.key === key && Date.now() - cached.at < CACHE_MS) {
        setCatalog(cached.catalog);
        return;
      }
      const result = await fetchBookingExtras(token, lang);
      if (!alive) return;
      if (result.success) cached = { key, at: Date.now(), catalog: result };
      setCatalog(result);
    };
    load();
    const onUpdate = () => { cached = null; load(); };
    window.addEventListener('nva_extras_update', onUpdate);
    return () => { alive = false; window.removeEventListener('nva_extras_update', onUpdate); };
  }, [token, lang, key]);
  return catalog;
};

export default useExtrasPreview;
